import { useState, type ReactElement } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'

import { CoaTemplateForm, coaTemplateQuery, useUpdateCoaTemplate } from '~/features/coa-templates'
import type { AppError } from '~/lib/result'

/**
 * `/admin/finance/reference-accounts/$templateId/edit` — edit an existing
 * Chart-of-Accounts Templates row. A full page rather than a modal, same
 * reasoning as the `new` route.
 */
export const Route = createFileRoute(
  '/admin/finance/reference-accounts/$templateId/edit',
)({
  component: EditTemplatePage,
})

function EditTemplatePage(): ReactElement {
  const { templateId } = Route.useParams()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)

  const templateQuery = useQuery(coaTemplateQuery(templateId))
  const updateMutation = useUpdateCoaTemplate()

  const template = templateQuery.data

  if (templateQuery.isPending) {
    return (
      <div className="ka-page" aria-busy="true">
        <div className="ka-sk ka-sk--line-flex-2" />
      </div>
    )
  }
  if (templateQuery.error || !template) {
    return (
      <div className="ka-page">
        <div className="ka-card">
          <div className="ka-empty">
            <div className="ka-empty-title">Template not found</div>
            <Link to="/admin/finance/reference-accounts" className="ka-btn ka-btn--secondary ka-btn--sm">
              <ArrowLeft size={14} aria-hidden="true" /> Back to templates
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const backTo = () =>
    void navigate({
      to: '/admin/finance/reference-accounts/$templateId',
      params: { templateId },
    })

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm" onClick={backTo}>
            <ArrowLeft size={14} aria-hidden="true" /> Back
          </button>
          <h1 className="ka-page-header-title">Edit {template.name}</h1>
        </div>
      </header>

      <CoaTemplateForm
        template={template}
        isSubmitting={updateMutation.isPending}
        serverError={serverError}
        onCancel={backTo}
        onSubmit={(body) => {
          setServerError(null)
          updateMutation.mutate(
            { templateId, body: body as Parameters<typeof updateMutation.mutate>[0]['body'] },
            {
              onSuccess: backTo,
              onError: (cause) => setServerError(errorToMessage(cause)),
            },
          )
        }}
      />
    </div>
  )
}

function errorToMessage(cause: unknown): string {
  const appError = cause as AppError
  return appError?.message ?? 'Something went wrong. Please try again.'
}
